// What the insert menu (the round "+") offers, each with its keyboard
// shortcut, and the shortcuts for what isn't in it. The "/" menu is for your
// thoughts only (SlashMenu.tsx).
//
// The menu (Niki & Andrew, 10/2) is blocks only, eight of them, no headers:
// Thought, Margin note and Image first (the first-class three), a quiet rule,
// then Scene, Voice memo, Link, Collection and Divider. Headings and Quote
// left it ("those are options after you've already written them"): they're
// in the selection toolbar and keep their shortcuts. Columns are made by
// dragging, and keep ⌥⌘\ as the keyboard way. Pins are margin notes now.

export type InsertChoice =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'pullquote'
  | 'thought'
  | 'margin'
  | 'image'
  | 'scene'
  | 'voice'
  | 'linkcard'
  | 'collection'
  | 'divider'
  | 'columns'

/** The heading level a choice inserts, or null for anything else. */
export function choiceHeadingLevel(c: InsertChoice): 1 | 2 | 3 | null {
  return c === 'heading1' ? 1 : c === 'heading2' ? 2 : c === 'heading3' ? 3 : null
}

/** Choices that start from one of your thoughts (or your own words) and open the thought picker. */
export type ThoughtChoice = Extract<InsertChoice, 'thought' | 'margin'>

export function isThoughtChoice(c: InsertChoice): c is ThoughtChoice {
  return c === 'thought' || c === 'margin'
}

export interface InsertChoiceDef {
  type: InsertChoice
  label: string
  hint?: string
  /** Extra words the "/" filter matches. */
  keywords: string
}

export interface InsertSection {
  id: string
  /** A small-caps header. The insert menu has none: its groups are split by a quiet rule. */
  title?: string
  items: InsertChoiceDef[]
  /** Fill this many rows down the first column before the second, instead of row by row. */
  stack?: number
}

export const INSERT_SECTIONS: InsertSection[] = [
  {
    id: 'first',
    items: [
      { type: 'thought', label: 'Thought', hint: 'Sourced, in the column', keywords: 'quote source saved' },
      { type: 'margin', label: 'Margin note', hint: 'Beside the text, or pinned across several', keywords: 'aside side note pin pinned sticky' },
      { type: 'image', label: 'Image', hint: 'In the column or wrapped', keywords: 'photo picture img wrap resize' },
    ],
  },
  {
    id: 'more',
    items: [
      { type: 'scene', label: 'Scene', hint: 'Full-bleed break, words over it', keywords: 'bleed break banner full' },
      { type: 'voice', label: 'Voice memo', hint: 'Your voice, in the margin', keywords: 'audio record aside' },
      { type: 'linkcard', label: 'Link', hint: 'A card for a web page', keywords: 'url link card bookmark embed' },
      { type: 'collection', label: 'Collection', hint: 'A card for a collection', keywords: 'collection burrow related adjacent more' },
      { type: 'divider', label: 'Divider', hint: 'A rule across the column', keywords: 'break rule line hr' },
    ],
  },
]

/**
 * Shortcuts with no row in the menu: headings and Quote turn the paragraph
 * you're in (the selection toolbar has them too), and Columns, otherwise made
 * by dragging a block beside another, start an empty pair.
 */
export const SHORTCUT_ONLY: InsertChoiceDef[] = [
  { type: 'heading1', label: 'Heading 1', keywords: 'h1 heading' },
  { type: 'heading2', label: 'Heading 2', keywords: 'h2 heading' },
  { type: 'heading3', label: 'Heading 3', keywords: 'h3 heading' },
  { type: 'pullquote', label: 'Quote', keywords: 'quote pull' },
  { type: 'columns', label: 'Columns', keywords: 'column side grid' },
]

// ── Keyboard shortcuts ───────────────────────────────────────────────────
// Two ways in, one scheme:
// - Anywhere in the text, ⌥⌘ (Ctrl+Alt elsewhere) and one key. Blocks take a
//   letter that names them (T thought, M margin note, G graphic, S scene,
//   V voice, K link as ⌘K links words, O collection, - divider); what you've
//   already written takes a digit or a sign, the scheme Google Docs uses for
//   headings (1 2 3 headings, Q quote, \ columns).
// - With the "+" menu open, a plain digit picks that row, 1–8 top to bottom
//   (menuDigit below); each row shows its number.
// Matched on the physical key (KeyboardEvent.code), since Option turns letters
// into other characters on a Mac. Letters the browsers keep for themselves
// under ⌥⌘ (I, J, C, U devtools; L downloads; B bookmarks) and macOS takes
// (D the Dock, H hide) are avoided.

export const SHORTCUT_KEY: Record<InsertChoice, { code: string; label: string }> = {
  heading1: { code: 'Digit1', label: '1' },
  heading2: { code: 'Digit2', label: '2' },
  heading3: { code: 'Digit3', label: '3' },
  pullquote: { code: 'KeyQ', label: 'Q' },
  thought: { code: 'KeyT', label: 'T' },
  margin: { code: 'KeyM', label: 'M' },
  image: { code: 'KeyG', label: 'G' },
  scene: { code: 'KeyS', label: 'S' },
  voice: { code: 'KeyV', label: 'V' },
  linkcard: { code: 'KeyK', label: 'K' },
  collection: { code: 'KeyO', label: 'O' },
  divider: { code: 'Minus', label: '-' },
  columns: { code: 'Backslash', label: '\\' },
}

/**
 * A plain digit pressed in the open "+" menu (1–9, top row or keypad, by
 * the physical key so AZERTY works too): the row it picks, from 1. Null
 * for anything else, and for a digit with ⌘, Ctrl or ⌥ held.
 */
export function menuDigit(e: Pick<KeyboardEvent, 'key' | 'code' | 'ctrlKey' | 'metaKey' | 'altKey'>): number | null {
  if (e.metaKey || e.ctrlKey || e.altKey) return null
  const m = /^(?:Digit|Numpad)([1-9])$/.exec(e.code) ?? /^([1-9])$/.exec(e.key)
  return m ? Number(m[1]) : null
}

/** Apple keyboards say ⌘ and ⌥; everything else Ctrl and Alt. */
export function isApple(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '')
}

/** The key chip's text for a choice: "⌥⌘1" on a Mac, "Ctrl+Alt+1" elsewhere. */
export function shortcutLabel(c: InsertChoice, apple = isApple()): string {
  const key = SHORTCUT_KEY[c].label
  return apple ? `⌥⌘${key}` : `Ctrl+Alt+${key}`
}

/** The same, for aria-keyshortcuts. */
export function shortcutAria(c: InsertChoice): string {
  const key = SHORTCUT_KEY[c].label
  return `Meta+Alt+${key} Control+Alt+${key}`
}

type KeyLike = Pick<KeyboardEvent, 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'> & { getModifierState?: (k: 'AltGraph') => boolean }

/** The choice a key press asks for, or null. AltGr (Ctrl+Alt on Windows) typing a character isn't a shortcut. */
export function choiceForKey(e: KeyLike): InsertChoice | null {
  if (!(e.metaKey || e.ctrlKey) || !e.altKey || e.shiftKey) return null
  if (e.getModifierState?.('AltGraph')) return null
  for (const c of Object.keys(SHORTCUT_KEY) as InsertChoice[]) if (SHORTCUT_KEY[c].code === e.code) return c
  return null
}

/** ⌘K / Ctrl+K: link the selected words. */
export function isLinkKey(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): boolean {
  return (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k'
}

/**
 * What can't go inside a column cell: columns don't nest, a scene spans the
 * page, and an image wraps rather than sitting in a column (Niki, 10/5).
 */
export const NOT_IN_A_CELL: ReadonlySet<InsertChoice> = new Set<InsertChoice>(['columns', 'scene', 'image'])

/** The sections without the choices in `exclude`. */
export function withoutChoices(sections: InsertSection[], exclude?: ReadonlySet<InsertChoice>): InsertSection[] {
  if (!exclude?.size) return sections
  return sections.map((s) => ({ ...s, items: s.items.filter((c) => !exclude.has(c.type)) })).filter((s) => s.items.length > 0)
}

export const INSERT_CHOICES: InsertChoiceDef[] = INSERT_SECTIONS.flatMap((s) => s.items)

/** Sections filtered to the choices matching a "/" query (every choice when the query is empty). */
export function matchSections(query: string, exclude?: ReadonlySet<InsertChoice>): InsertSection[] {
  const sections = withoutChoices(INSERT_SECTIONS, exclude)
  const q = query.trim().toLowerCase()
  if (!q) return sections
  const hit = (c: InsertChoiceDef) =>
    c.label.toLowerCase().includes(q) || c.keywords.split(' ').some((k) => k.startsWith(q))
  return sections.map((s) => ({ ...s, items: s.items.filter(hit) })).filter((s) => s.items.length > 0)
}

/**
 * Keyboard movement through a menu of `count` options laid out in `columns`
 * columns: arrows move by a row or a column, wrapping at the ends. Returns
 * null for keys it doesn't handle.
 */
export function moveIndex(key: string, index: number, count: number, columns = 1): number | null {
  if (count <= 0) return null
  const step =
    key === 'ArrowDown' ? columns : key === 'ArrowUp' ? -columns : columns > 1 && key === 'ArrowRight' ? 1 : columns > 1 && key === 'ArrowLeft' ? -1 : 0
  if (step === 0) {
    if (key === 'Home') return 0
    if (key === 'End') return count - 1
    return null
  }
  const next = index + step
  if (next >= count) return step > 1 && index < count - 1 ? count - 1 : 0
  if (next < 0) return step < -1 && index > 0 ? 0 : count - 1
  return next
}
